/**
 * Main Application Component for IT Maturity Assessment
 *
 * Root component handling routing and route protection.
 * Authentication state is provided by AuthenticationContext (in main.tsx).
 * Session timeout is managed inside AuthenticationProvider.
 *
 * Author: Verto Wave
 * Created: 2025
 */

import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { useAuthentication } from '@/features/auth/AuthenticationContext'

// =============================================================================
// LAYOUT COMPONENTS
// =============================================================================

import { AuthLayout } from '@/components/layouts/auth-layout'
import { DashboardLayout } from '@/components/layouts/dashboard-layout'

// =============================================================================
// AUTHENTICATION PAGES
// =============================================================================

import { LoginPage } from '@/pages/auth/login-page'
import { SetupPage } from '@/pages/auth/setup-page'
import { getAppConfig } from '@/lib/app-config'

// =============================================================================
// DASHBOARD PAGES
// =============================================================================

import { DashboardPage } from '@/pages/dashboard/dashboard-page'
import { OrganizationsPage } from '@/pages/dashboard/organizations-page'
import { OrganizationDetailPage } from '@/pages/dashboard/organization-detail-page'
import { AssessmentsPage } from '@/pages/dashboard/assessments-page'
import AttachmentsPage from '@/pages/assessments/attachments-page'

import { QuestionnairePage } from '@/pages/dashboard/questionnaire-page'
import { ResultsPage } from '@/pages/dashboard/results-page'
import { ReportPage } from '@/pages/dashboard/report-page'
import { ProfilePage } from '@/pages/dashboard/profile-page'



// =============================================================================
// SETTINGS PAGES
// =============================================================================

import { SettingsPage } from '@/pages/settings/settings-page'
import { SettingsUsersPage } from '@/pages/settings/settings-users-page'
import { SettingsIndustriesPage } from '@/pages/settings/settings-industries-page'
import { SettingsRecommendationsPage } from '@/pages/settings/settings-recommendations-page'
import { SettingsAssessmentEnginePage } from '@/pages/settings/settings-assessment-engine-page'
import { SettingsRolesPage } from '@/pages/settings/settings-roles-page'
import { SettingsSecurityPage } from '@/pages/settings/settings-security-page'
import { SettingsInitiativeDetailPage } from '@/pages/settings/settings-initiative-detail-page'

// =============================================================================
// AUDIT TRAIL PAGE
// =============================================================================

import { AuditTrailPage } from '@/pages/audit-trail/audit-trail-page'

// =============================================================================
// AI
// =============================================================================

import { AIAssistantPage } from '@/pages/dashboard/ai-assistant-page'

// =============================================================================
// AUTHORIZATION
// =============================================================================

import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { Unauthorized } from '@/components/ui/unauthorized'

// =============================================================================
// UI COMPONENTS
// =============================================================================

import { LoadingSpinner } from '@/components/ui/loading-spinner'

/**
 * RedirectAfterLogin
 * Checks if there's a saved redirect path from before login and navigates there.
 * Otherwise renders the DashboardPage.
 */
function RedirectAfterLogin() {
  const savedPath = sessionStorage.getItem('redirectAfterLogin')
  if (savedPath && savedPath !== '/' && savedPath !== '/auth/login') {
    sessionStorage.removeItem('redirectAfterLogin')
    return <Navigate to={savedPath} replace />
  }
  sessionStorage.removeItem('redirectAfterLogin')
  return <DashboardPage />
}

/**
 * Main App Component
 *
 * Reads authentication state from AuthenticationContext (provided in main.tsx).
 * No useEffect needed — the provider verifies the session on mount.
 */
function App() {
  const { isAuthenticated, isLoading } = useAuthentication()
  const location = useLocation()

  // Save intended path when not authenticated so we can redirect after login
  const needsSetup = getAppConfig().needsSetup
  if (!isLoading && !isAuthenticated && location.pathname !== '/auth/login' && location.pathname !== '/auth/setup') {
    sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search)
  }

  // Show loading spinner while verifying the session
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Routes>
        {/* PUBLIC ROUTES */}

        {/* AUTHENTICATION ROUTES */}
        {!isAuthenticated ? (
          <Route path="/auth/*" element={<AuthLayout />}>
            {needsSetup ? (
              <>
                <Route path="setup" element={<SetupPage />} />
                <Route path="*" element={<Navigate to="/auth/setup" replace />} />
              </>
            ) : (
              <>
                <Route path="login" element={<LoginPage />} />
                <Route path="*" element={<Navigate to="/auth/login" replace />} />
              </>
            )}
          </Route>
        ) : null}

        {/* PROTECTED ROUTES */}
        {isAuthenticated ? (
          <>
          {/* Report page renders OUTSIDE DashboardLayout for clean print */}
          <Route path="/assessments/:id/report" element={<ReportPage />} />
          <Route path="/*" element={<DashboardLayout />}>
            <Route index element={<RedirectAfterLogin />} />

            <Route path="organizations" element={
              <AuthorizationGate actions={['view_organization']} fallback={<Unauthorized />}>
                <OrganizationsPage />
              </AuthorizationGate>
            } />
            <Route path="organizations/:id" element={
              <AuthorizationGate actions={['view_organization']} fallback={<Unauthorized />}>
                <OrganizationDetailPage />
              </AuthorizationGate>
            } />

            <Route path="assessments" element={
              <AuthorizationGate actions={['view_assessment']} fallback={<Unauthorized />}>
                <AssessmentsPage />
              </AuthorizationGate>
            } />
            <Route path="assessments/attachments" element={
              <AuthorizationGate actions={['view_assessment']} fallback={<Unauthorized />}>
                <AttachmentsPage />
              </AuthorizationGate>
            } />

            <Route path="assessments/:id/questionnaire" element={
              <AuthorizationGate actions={['view_assessment']} fallback={<Unauthorized />}>
                <QuestionnairePage />
              </AuthorizationGate>
            } />
            <Route path="assessments/:id/results" element={
              <AuthorizationGate actions={['view_assessment']} fallback={<Unauthorized />}>
                <ResultsPage />
              </AuthorizationGate>
            } />


            <Route path="profile" element={<ProfilePage />} />

            <Route path="settings" element={
              <AuthorizationGate actions={['update_settings', 'view_settings']} mode="any" fallback={<Unauthorized />}>
                <SettingsPage />
              </AuthorizationGate>
            } />
            <Route path="settings/users" element={
              <AuthorizationGate actions={['view_all_users']} fallback={<Unauthorized />}>
                <SettingsUsersPage />
              </AuthorizationGate>
            } />
            <Route path="settings/industries" element={
              <AuthorizationGate actions={['view_industries', 'manage_industries']} mode="any" fallback={<Unauthorized />}>
                <SettingsIndustriesPage />
              </AuthorizationGate>
            } />
            <Route path="settings/recommendations" element={
              <AuthorizationGate actions={['manage_industries', 'manage_initiatives']} mode="any" fallback={<Unauthorized />}>
                <SettingsRecommendationsPage />
              </AuthorizationGate>
            } />
            <Route path="settings/recommendations/initiatives/:id" element={
              <AuthorizationGate actions={['manage_industries', 'manage_initiatives']} mode="any" fallback={<Unauthorized />}>
                <SettingsInitiativeDetailPage />
              </AuthorizationGate>
            } />
            <Route path="settings/assessment-engine" element={
              <AuthorizationGate actions={['view_domains', 'view_questions', 'view_maturity_levels', 'view_scoring_labels']} mode="any" fallback={<Unauthorized />}>
                <SettingsAssessmentEnginePage />
              </AuthorizationGate>
            } />
            <Route path="settings/roles" element={
              <AuthorizationGate actions={['view_actions']} fallback={<Unauthorized />}>
                <SettingsRolesPage />
              </AuthorizationGate>
            } />
            <Route path="settings/security" element={
              <AuthorizationGate actions={['view_all_users', 'view_actions']} mode="any" fallback={<Unauthorized />}>
                <SettingsSecurityPage />
              </AuthorizationGate>
            } />
            <Route path="audit-trail" element={
              <AuthorizationGate actions={['view_audit_trail']} fallback={<Unauthorized />}>
                <AuditTrailPage />
              </AuthorizationGate>
            } />

            <Route path="ai" element={
              <AuthorizationGate actions={['use_ai_chat']} fallback={<Unauthorized />}>
                <AIAssistantPage />
              </AuthorizationGate>
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
          </>
        ) : null}

        {/* DEFAULT REDIRECT */}
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? "/" : "/auth/login"} replace />}
        />
      </Routes>

      <Toaster />
    </div>
  )
}

export default App
